import { useState } from "react";
import { API } from "../lib/api";
import { CaretLeft, CaretRight, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Inline carousel for 1..3 menu images in the customer truck detail card.
 * - Single image: just shows it full-width.
 * - Multiple: horizontal snap-scroll strip + small dot indicators. No arrows
 *   inline (too crowded on mobile); arrows appear in the fullscreen lightbox.
 * - Tap any image → opens the full-screen lightbox at that index.
 */
export function MenuImagesCarousel({ paths = [], onOpen }) {
  if (!paths || paths.length === 0) return null;

  const single = paths.length === 1;

  return (
    <div className="mt-4 -mx-5 sm:mx-0">
      <div
        className={`flex gap-2 overflow-x-auto snap-x snap-mandatory scroll-px-5 px-5 sm:px-0 pb-1 ${
          single ? "sm:rounded-xl" : ""
        }`}
        style={{ scrollbarWidth: "none" }}
        data-testid="menu-carousel"
      >
        {paths.map((p, i) => (
          <button
            key={p}
            type="button"
            onClick={() => onOpen(i)}
            className={`snap-start shrink-0 rounded-xl overflow-hidden relative group ${
              single ? "w-full" : "w-[85%] sm:w-[70%]"
            }`}
            aria-label={`Open menu image ${i + 1}`}
            data-testid={`menu-carousel-item-${i}`}
          >
            <img
              src={`${API}/files/${p}`}
              alt={`Menu ${i + 1}`}
              loading="lazy"
              className="w-full max-h-72 object-cover transition group-hover:opacity-90"
            />
            {!single && (
              <div className="absolute bottom-2 right-2 rounded-full bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 backdrop-blur-md">
                {i + 1} / {paths.length}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Full-screen lightbox with next/prev navigation (and touch swipe via
 * framer-motion drag). Controls hide themselves when only one image exists.
 */
export function MenuImagesLightbox({ paths = [], startIndex = 0, onClose }) {
  const [i, setI] = useState(startIndex);

  if (!paths || paths.length === 0) return null;

  const last = paths.length - 1;
  const prev = () => setI((n) => (n <= 0 ? last : n - 1));
  const next = () => setI((n) => (n >= last ? 0 : n + 1));
  const multiple = paths.length > 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="fixed inset-0 z-50 bg-black/95 grid place-items-center p-4"
      onClick={onClose}
      data-testid="menu-lightbox"
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition z-10"
        aria-label="Close"
        data-testid="lightbox-close-btn"
      >
        <X className="h-5 w-5" />
      </button>

      {multiple && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-2 sm:left-6 h-11 w-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition z-10"
            aria-label="Previous image"
            data-testid="lightbox-prev-btn"
          >
            <CaretLeft weight="bold" className="h-5 w-5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-2 sm:right-6 h-11 w-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition z-10"
            aria-label="Next image"
            data-testid="lightbox-next-btn"
          >
            <CaretRight weight="bold" className="h-5 w-5" />
          </button>
          <div
            className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10"
            data-testid="lightbox-dots"
          >
            {paths.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${
                  idx === i ? "w-6 bg-white" : "w-1.5 bg-white/40"
                }`}
              />
            ))}
          </div>
        </>
      )}

      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={paths[i]}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          drag={multiple ? "x" : false}
          dragConstraints={{ left: 0, right: 0 }}
          onDragEnd={(_, info) => {
            if (info.offset.x < -80) next();
            else if (info.offset.x > 80) prev();
          }}
          className="max-w-full max-h-full overflow-auto touch-pan-y"
          style={{ touchAction: multiple ? "pan-y" : "pinch-zoom" }}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={`${API}/files/${paths[i]}`}
            alt={`Menu ${i + 1}`}
            className="max-w-[95vw] max-h-[85vh] object-contain select-none"
            draggable={false}
            data-testid={`lightbox-image-${i}`}
          />
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
